import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import {
  ensureBotConfigForRoutePath,
  getBotRoutePath,
  normalizeDirectUrlPath,
  resolveBotEntryFromRoutePath,
} from '../../config/getBotConfig';
import { fetchStaticJson, resolveAppStaticUrl } from '../../config/fetchStaticJson';
import { setCurrentConfig } from '../../config/currentBotConfig';
import { BotConfigProvider } from '../../context/BotConfigContext';
import Chat from '../chat/Chat';
import applyTheme from '../../theme/applyTheme';
import useLayoutService from '../../services/layout/layoutService';
import AdminPanel from './adminPanel/AdminPanel';
import { buildExportPayload } from './adminDraftPayload';
import { validateDiscoveryButtons } from './discoveryButtonsValidation';
import { ADMIN_IMAGE_ASSET_KEYS } from './botIconLocalStorage';
import {
  BOTS_IN_VC_FILE_NAME,
  fetchBotsInVcForSave,
  fetchTeachingAssistantBotsList,
  syncBotsListFromDraft,
  upsertBotsListFromDraft,
} from './teachingAssistantBotsList';
import { normalizeBadgeFields } from './badgeIcon';
import classes from './AdminDesignerPage.module.css';
import appClasses from '../App.module.css';

function domainsUrl() {
  return resolveAppStaticUrl(window.config?.domains, '/jsons/domains.json');
}

/** Fill subject fields from the route/folder domain when the bot config omits them. */
async function hydrateSubjectFromDomain(draft, domainName) {
  if (!draft || !domainName) return draft;
  const hasSubject =
    (typeof draft.subjectName === 'string' && draft.subjectName.trim()) ||
    (typeof draft.subjectImageUrl === 'string' && draft.subjectImageUrl.trim());
  if (hasSubject && typeof draft.domain === 'string' && draft.domain.trim()) {
    return draft;
  }
  try {
    const data = await fetchStaticJson(domainsUrl());
    const list = Array.isArray(data?.domains) ? data.domains : [];
    const match = list.find((d) => d.name === domainName);
    if (!match) {
      return { ...draft, domain: draft.domain || domainName };
    }
    return {
      ...draft,
      domain: draft.domain || match.name,
      subjectName: draft.subjectName || match.text?.he || match.name,
      subjectImageUrl: draft.subjectImageUrl || match.icon || '',
    };
  } catch {
    return { ...draft, domain: draft.domain || domainName };
  }
}

function registryUrlPathForBot(domainName, botName, cfg) {
  const entry = window.config?.botsList?.[domainName]?.bots?.[botName];
  if (entry && typeof entry === 'object' && typeof entry.urlPath === 'string' && entry.urlPath.trim()) {
    return normalizeDirectUrlPath(entry.urlPath);
  }
  if (typeof cfg?.urlPath === 'string' && cfg.urlPath.trim()) {
    return normalizeDirectUrlPath(cfg.urlPath);
  }
  return '';
}

function createBaseDraft(cfg, domainName, botName) {
  const urlPath = registryUrlPathForBot(domainName, botName, cfg);
  const routePath = getBotRoutePath(domainName, botName, urlPath);
  const path = `/admin/${routePath}`;
  const badge = normalizeBadgeFields(cfg);
  return {
    ...JSON.parse(JSON.stringify(cfg)),
    ...badge,
    domain: (typeof cfg?.domain === 'string' && cfg.domain.trim()) || domainName,
    urlPath,
    key: botName,
    botUrl: window.location.origin + path,
    isDesignPreview: true,
    discoveryButton: Array.isArray(cfg?.discoveryButton)
      ? JSON.parse(JSON.stringify(cfg.discoveryButton))
      : [],
  };
}

function collectOriginalImageUrls(draft) {
  return ADMIN_IMAGE_ASSET_KEYS.reduce((acc, key) => {
    acc[key] = draft?.[key] ?? '';
    return acc;
  }, {});
}

function downloadJson(filename, dataObj) {
  const json = JSON.stringify(dataObj, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 1500);
}

export default function AdminDesignerPage() {
  const { '*': routeSplat } = useParams();
  const routePath = normalizeDirectUrlPath(routeSplat);
  const { texts } = useSelector((state) => state.translate);
  useLayoutService();

  const [domainName, setDomainName] = useState('');
  const [botName, setBotName] = useState('');
  const [draft, setDraft] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [exportError, setExportError] = useState('');
  const [originalImageUrls, setOriginalImageUrls] = useState({});
  const [teachingAssistantBotsList, setTeachingAssistantBotsList] = useState([]);
  const [botsListLoaded, setBotsListLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setDraft(null);
    setExportError('');
    setBotsListLoaded(false);
    setDomainName('');
    setBotName('');

    if (!routePath) {
      setLoadState('error');
      return undefined;
    }

    const entry = resolveBotEntryFromRoutePath(routePath);
    if (!entry) {
      setLoadState('error');
      return undefined;
    }

    const resolvedDomain = entry.projectKey;
    const resolvedBot = entry.resolvedBotName;
    setDomainName(resolvedDomain);
    setBotName(resolvedBot);

    Promise.all([
      ensureBotConfigForRoutePath(routePath),
      fetchTeachingAssistantBotsList().catch(() => []),
    ])
      .then(([cfg, botsList]) => {
        if (cancelled) return;
        if (!cfg) {
          setLoadState('error');
          return;
        }

        setTeachingAssistantBotsList(botsList);
        setBotsListLoaded(true);

        return hydrateSubjectFromDomain(
          createBaseDraft(cfg, resolvedDomain, resolvedBot),
          resolvedDomain
        ).then((baseDraft) => {
          if (cancelled) return;
          setOriginalImageUrls(collectOriginalImageUrls(baseDraft));

          const synced = syncBotsListFromDraft(botsList, baseDraft, {
            domainName: resolvedDomain,
            botName: resolvedBot,
            subjectDomain: typeof baseDraft.domain === 'string' ? baseDraft.domain : '',
          });
          setTeachingAssistantBotsList(synced.botsList);

          setDraft(baseDraft);
          setLoadState('ready');
        });
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [routePath]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const botIconUrl = draft?.botIconUrl;
  const draftKey = draft?.key;

  useEffect(() => {
    if (loadState !== 'ready') return;
    if (typeof botIconUrl !== 'string' || !botIconUrl || botIconUrl.startsWith('data:')) return;
    setTeachingAssistantBotsList((botsList) => {
      const { botsList: next } = syncBotsListFromDraft(
        botsList,
        { key: draftKey, botIconUrl },
        {
          domainName,
          botName,
          resolvedBotIconUrl: botIconUrl,
        }
      );
      return next;
    });
  }, [botIconUrl, draftKey, domainName, botName, loadState]);

  const previewRef = useRef(null);
  const [sidebarEl, setSidebarEl] = useState(null);

  useEffect(() => {
    if (draft) setCurrentConfig(draft);
  }, [draft]);

  useEffect(() => {
    const el = previewRef.current;
    if (el && draft?.theme) applyTheme(draft.theme, el);
  }, [draft?.theme, draft]);

  const onDiscoveryButtonsChange = useCallback((buttons) => {
    setExportError('');
    setDraft((d) => ({
      ...d,
      discoveryButton: buttons,
    }));
  }, []);

  const onSubjectSelect = useCallback(
    ({ domain }) => {
      setExportError('');
      setTeachingAssistantBotsList((botsList) => {
        const { botsList: next } = syncBotsListFromDraft(botsList, draft, {
          domainName,
          botName,
          subjectDomain: typeof domain === 'string' ? domain : '',
        });
        return next;
      });
    },
    [draft, domainName, botName]
  );

  const onExportConfig = useCallback(() => {
    if (!draft || !botName) {
      setExportError('אין טיוטה לטעינה — רענני את העמוד');
      return;
    }

    try {
      const discovery = validateDiscoveryButtons(draft.discoveryButton ?? [], texts);
      if (!discovery.ok) {
        setExportError(discovery.error || 'כפתורי Discovery לא תקינים');
        return;
      }

      setExportError('');
      const payload = buildExportPayload(draft, { botName, domainName });
      downloadJson(`${botName}_config.json`, payload);
    } catch (err) {
      setExportError(err?.message || 'נכשלה הורדת JSON');
    }
  }, [draft, botName, domainName, texts]);

  const mergeBotsInVcForSave = useCallback(async () => {
    if (!draft) {
      setExportError(texts.botsInVcLoadFailed);
      return null;
    }
    const source = await fetchBotsInVcForSave(teachingAssistantBotsList);
    if (!source) {
      setExportError(texts.botsInVcLoadFailed);
      return null;
    }
    const payload = buildExportPayload(draft, { botName, domainName });
    const result = upsertBotsListFromDraft(source, draft, {
      domainName,
      botName,
      resolvedBotIconUrl: payload?.botIconUrl,
      ...(typeof draft.domain === 'string' ? { subjectDomain: draft.domain } : {}),
    });
    setTeachingAssistantBotsList(result.botsList);
    setExportError('');
    return result.botsList;
  }, [draft, teachingAssistantBotsList, botName, domainName, texts]);

  const onExportBotsInVc = useCallback(async () => {
    try {
      const botsList = await mergeBotsInVcForSave();
      if (!botsList) return;
      downloadJson(BOTS_IN_VC_FILE_NAME, botsList);
    } catch (err) {
      setExportError(err?.message || texts.botsInVcDownloadFailed);
    }
  }, [mergeBotsInVcForSave, texts]);

  const getBotsInVcContent = useCallback(async () => {
    return mergeBotsInVcForSave();
  }, [mergeBotsInVcForSave]);

  if (loadState === 'loading') {
    return null;
  }

  if (loadState === 'error' || !draft) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className={classes.Layout}>
      <aside ref={setSidebarEl} className={classes.Sidebar}>
        <AdminPanel
          key={`${domainName}-${botName}`}
          domainName={domainName}
          botName={botName}
          draft={draft}
          setDraft={setDraft}
          discoveryButtons={draft.discoveryButton ?? []}
          onDiscoveryButtonsChange={onDiscoveryButtonsChange}
          originalImageUrls={originalImageUrls}
          onExportConfig={onExportConfig}
          onExportBotsInVc={onExportBotsInVc}
          canExportBotsInVc={botsListLoaded}
          getBotsInVcContent={getBotsInVcContent}
          exportError={exportError}
          modalContainer={sidebarEl}
          onSubjectSelect={onSubjectSelect}
        />
      </aside>
      <div ref={previewRef} className={classes.Preview}>
        <div className={classes.PreviewInner}>
          <BotConfigProvider value={draft}>
            <div className={appClasses.App}>
              <Chat />
            </div>
          </BotConfigProvider>
        </div>
      </div>
    </div>
  );
}
